import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Breadcrumbs,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  type SvgIconProps,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import type { RootState } from '../../../store';
import {
  ROTULO_CONSUMO,
  useAdminListarRecorteCatalogoQuery,
  useAdminSalvarRecorteCatalogoMutation,
  type ColunaRecorte,
  type ConsumoTotem,
  type GrupoRecorte,
  type ItemRecorte,
  type OpcaoRecorte,
  type PassoRecorte,
  type ProdutoRecorte,
} from '../../../api';
import { HeaderTela } from '../../../components/admin/header-tela';
import { PainelInfo } from '../../../components/admin/painel-info';
import {
  aplicar,
  contarAlteracoes,
  estaForaNoIndice,
  indiceForaPorItem,
  type TipoItemRecorte,
} from '../../../lib/recorte-visibilidade';

/**
 * Visibilidade dos itens por ponto de acesso.
 *
 * O cadastro (grupos e produtos) é do Sischef e é o mesmo para todas as lojas
 * da Unidade — aqui o gestor escolhe onde cada item aparece NESTA loja. Tudo
 * que está cadastrado aparece por padrão; a tela grava exclusões (ver
 * `item-oculto-cardapio.ts`). Nada é alterado no cadastro do Sischef.
 *
 * Só entra na lista o que o Sischef marcou como "Mostrar no cardápio": insumo e
 * uso e consumo não aparecem nem para serem ocultados.
 *
 * A tela tem dois níveis. Primeiro um painel com os pontos de acesso da loja e
 * quanto cada um esconde — é ele que responde "por onde começo": mostra onde
 * dá para mexer e onde já mexeram, sem exigir que o gestor adivinhe a
 * taxonomia tipo/ponto em dois selects vazios. Clicando em um ponto vai para a
 * lista Grupo > Produto daquele ponto.
 *
 * O rascunho é da loja inteira, não do ponto: dá para ajustar vários pontos e
 * salvar uma vez só, e voltar ao painel não perde nada.
 *
 * A loja tem vários pontos do mesmo tipo (dois totens, quatro checkouts, mais
 * de um link de delivery) e cada um pode ter cardápio próprio. Mesa, comanda e
 * tablet entram como um ponto só ("Todas as mesas"): são o mesmo atendimento e
 * o cardápio do salão é um só. Uma grade com todos os pontos como coluna foi
 * tentada antes e ficou ilegível.
 */

const ROTULO_CURTO: Record<ConsumoTotem, string> = { LOCAL: 'no local', LEVAR: 'para levar' };

interface ContagemOcultos {
  grupos: number;
  itens: number;
  opcoes: number;
}

const VAZIO: ContagemOcultos = { grupos: 0, itens: 0, opcoes: 0 };

const totalOcultos = (c: ContagemOcultos): number => c.grupos + c.itens + c.opcoes;

const somarOcultos = (a: ContagemOcultos, b: ContagemOcultos): ContagemOcultos => ({
  grupos: a.grupos + b.grupos,
  itens: a.itens + b.itens,
  opcoes: a.opcoes + b.opcoes,
})

function partesDaContagem({ grupos, itens, opcoes }: ContagemOcultos): string[] {
  const partes: string[] = []

  if (grupos > 0) {
    partes.push(`${grupos} ${grupos === 1 ? 'grupo' : 'grupos'}`)
  }

  if (itens > 0) {
    partes.push(`${itens} ${itens === 1 ? 'item' : 'itens'}`)
  }

  if (opcoes > 0) {
    partes.push(`${opcoes} ${opcoes === 1 ? 'opção de passo' : 'opções de passo'}`)
  }

  return partes
}

function juntarPartes(partes: string[]): string {
  if (partes.length <= 1) {
    return partes.join('')
  }

  return `${partes.slice(0, -1).join(', ')} e ${partes[partes.length - 1]}`
}

function rotuloOcultos(contagem: ContagemOcultos): string {
  const partes = partesDaContagem(contagem)

  if (partes.length === 0) {
    return 'Cardápio completo'
  }

  const umSo = totalOcultos(contagem) === 1
  const soOpcoes = contagem.grupos === 0 && contagem.itens === 0
  const adjetivo = soOpcoes ? (umSo ? 'oculta' : 'ocultas') : (umSo ? 'oculto' : 'ocultos')

  return `${juntarPartes(partes)} ${adjetivo}`
}

function rotuloOcultosCurto(contagem: ContagemOcultos): string {
  const partes = partesDaContagem(contagem)

  return partes.length === 0 ? 'completo' : partes.join(', ')
}

/** Prefixo da seleção que vale para todos os pontos de um tipo. */
const PREFIXO_TIPO = 'tipo:';

/**
 * Teto para abrir os grupos sozinho quando há filtro. Busca de uma letra só
 * casa com o catálogo inteiro, e abrir tudo monta milhares de controles.
 */
const MAX_ABRIR_SOZINHO = 20;

type Tipo = TipoItemRecorte;

function chaveDoCartao(coluna: ColunaRecorte): string {
  return coluna.consumo === '*' ? coluna.chave : `ponto:${coluna.idIdentificador}`;
}

function rotuloSemConsumo(coluna: ColunaRecorte): string {
  if (coluna.consumo === '*') return coluna.rotulo;

  return coluna.rotulo.replace(` · ${ROTULO_CONSUMO[coluna.consumo]}`, '');
}

export function AdminCatalogoPage(): JSX.Element {
  const idConfig = useSelector((s: RootState) => s.adminAuth.idConfiguracaoSelecionada)!;
  const { data, isLoading } = useAdminListarRecorteCatalogoQuery(idConfig);
  const [salvar, statusSalvar] = useAdminSalvarRecorteCatalogoMutation();

  const grupos = useMemo(() => data?.grupos ?? [], [data]);
  const colunas = useMemo(() => data?.colunas ?? [], [data]);
  const iniciais = useMemo(() => data?.ocultos ?? [], [data]);

  const [idsDasOpcoes, idsDasOpcoesSemSubPasso] = useMemo(() => {
    const todas = new Set<number>();
    const semSubPasso = new Set<number>();
    for (const g of grupos) {
      for (const p of g.produtos) {
        for (const passo of p.passos ?? []) {
          for (const opcao of opcoesNaTela(passo, true)) {
            todas.add(opcao.id);
          }
          for (const opcao of opcoesNaTela(passo, false)) {
            semSubPasso.add(opcao.id);
          }
        }
      }
    }
    return [[...todas], [...semSubPasso]];
  }, [grupos]);

  const idsDasOpcoesNas = (colunasDaFaixa: ColunaRecorte[]): number[] =>
    mostraSubPasso(colunasDaFaixa) ? idsDasOpcoes : idsDasOpcoesSemSubPasso;

  // `null` = ainda não mexeu; aí vale o que veio do servidor.
  const [rascunho, setRascunho] = useState<ItemRecorte[] | null>(null);
  /** `''` = painel de pontos; senão `chave` da coluna ou `tipo:<aba>`. */
  const [selecao, setSelecao] = useState('');
  const [abertos, setAbertos] = useState<Set<number>>(new Set());
  const [passosAbertos, setPassosAbertos] = useState<Set<number>>(new Set());
  const [busca, setBusca] = useState('');
  const [soOcultos, setSoOcultos] = useState(false);
  const [indiceConsumo, setIndiceConsumo] = useState(0);
  const [salvo, setSalvo] = useState(false);

  const ocultos = rascunho ?? iniciais;

  /** Itens cujo recorte mudou — alimenta a barra de "não salvo". */
  const alteracoes = useMemo(
    () => (rascunho == null ? 0 : contarAlteracoes(iniciais, rascunho)),
    [rascunho, iniciais],
  );
  const alterado = alteracoes > 0;

  // Um ponto só na loja: o painel seria um card solitário: vai direto pra lista.
  useEffect(() => {
    if (!selecao && colunas.length === 1) setSelecao(colunas[0]!.chave);
  }, [selecao, colunas]);

  /** Pontos em que a marcação será escrita: um, ou todos os do tipo. */
  const alvos = useMemo(() => {
    if (!selecao) return [];
    if (selecao.startsWith(PREFIXO_TIPO)) {
      const aba = selecao.slice(PREFIXO_TIPO.length);
      return colunas.filter((c) => c.grupo === aba);
    }
    return colunas.filter((c) => chaveDoCartao(c) === selecao);
  }, [selecao, colunas]);

  const faixas = useMemo(() => {
    const porConsumo = new Map<ConsumoTotem, ColunaRecorte[]>();
    for (const c of alvos) {
      if (c.consumo === '*') continue;
      porConsumo.set(c.consumo, [...(porConsumo.get(c.consumo) ?? []), c]);
    }
    if (porConsumo.size === 0) return [{ consumo: null, rotulo: null, colunas: alvos }];
    return [...porConsumo.entries()].map(([consumo, colunasDaFaixa]) => ({
      consumo,
      rotulo: ROTULO_CONSUMO[consumo],
      colunas: colunasDaFaixa,
    }));
  }, [alvos]);

  const faixaAtiva = faixas[indiceConsumo] ?? faixas[0]!;
  const colunasAtivas = faixaAtiva.colunas;
  const outraFaixa = faixas.length > 1 ? faixas.find((f) => f !== faixaAtiva) : undefined;

  useEffect(() => setIndiceConsumo(0), [selecao]);

  /**
   * Índice do recorte por item. Sem ele cada linha varria a lista inteira de
   * marcações a cada render.
   */
  const foraPorItem = useMemo(() => indiceForaPorItem(ocultos), [ocultos]);

  /** Fora quando há marcação do ponto, do canal dele ou da loja inteira. */
  const estaFora = (t: Tipo, id: number, coluna: ColunaRecorte): boolean =>
    estaForaNoIndice(foraPorItem, t, id, coluna);

  /** Ligado = aparece em TODAS as colunas da faixa (por padrão, a que está na tela). */
  const estaLigado = (t: Tipo, id: number, colunasDaFaixa: ColunaRecorte[] = colunasAtivas): boolean =>
    colunasDaFaixa.every((c) => !estaFora(t, id, c));

  /**
   * Aviso de que o item também some no OUTRO cardápio do totem. É a segunda
   * informação sob demanda: aparece na linha de quem está fora lá, sem trazer
   * de volta o segundo interruptor.
   */
  const avisoOutraFaixa = outraFaixa?.consumo ? `Oculto ${ROTULO_CURTO[outraFaixa.consumo]}` : '';
  const ocultoNaOutra = (t: Tipo, id: number): boolean =>
    !!outraFaixa && !estaLigado(t, id, outraFaixa.colunas);

  /** O que não aparece numa coluna. Grupo fora conta pelo grupo, não pelos produtos. */
  const contarOcultos = (coluna: ColunaRecorte): ContagemOcultos => ({
    ...grupos.reduce(
      (total, g) =>
        estaFora('GRUPO', g.id, coluna)
          ? { ...total, grupos: total.grupos + 1 }
          : { ...total, itens: total.itens + g.produtos.filter((p) => estaFora('PRODUTO', p.id, coluna)).length },
      VAZIO,
    ),
    opcoes: idsDasOpcoesNas([coluna]).filter((id) => estaFora('OPCAO', id, coluna)).length,
  });

  const ocultosPorColuna = useMemo(() => {
    const mapa = new Map<string, ContagemOcultos>();
    for (const coluna of colunas) mapa.set(coluna.chave, contarOcultos(coluna));
    return mapa;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colunas, grupos, idsDasOpcoes, idsDasOpcoesSemSubPasso, foraPorItem]);

  /**
   * Cartões do painel, agrupados por tipo. As duas colunas de um totem viram um
   * cartão só, com a contagem de ocultos de cada consumo.
   */
  const pontosPorTipo = useMemo(() => {
    const mapa = new Map<string, { chave: string; rotulo: string; colunas: ColunaRecorte[] }[]>();
    for (const coluna of colunas) {
      const lista = mapa.get(coluna.grupo) ?? [];
      const chave = chaveDoCartao(coluna);
      const existente = lista.find((c) => c.chave === chave);
      if (existente) existente.colunas.push(coluna);
      else lista.push({ chave, rotulo: rotuloSemConsumo(coluna), colunas: [coluna] });
      mapa.set(coluna.grupo, lista);
    }
    return [...mapa.entries()];
  }, [colunas]);

  const definir = (
    itens: { tipo: Tipo; id: number }[],
    ligado: boolean,
    colunasDaFaixa: ColunaRecorte[] = colunasAtivas,
  ): void => {
    setRascunho((atual) => {
      let lista = atual ?? iniciais;
      for (const item of itens) lista = aplicar(lista, item.tipo, item.id, ligado, colunas, colunasDaFaixa);
      return lista;
    });
    setSalvo(false);
  };

  /** Todos os grupos e produtos do catálogo — alimenta os atalhos por consumo. */
  const todosOsItens = useMemo(
    () => [
      ...grupos.flatMap((g) => [
        { tipo: 'GRUPO' as Tipo, id: g.id },
        ...g.produtos.map((prod) => ({ tipo: 'PRODUTO' as Tipo, id: prod.id })),
      ]),
      ...idsDasOpcoes.map((id) => ({ tipo: 'OPCAO' as Tipo, id })),
    ],
    [grupos, idsDasOpcoes],
  );

  const alternarPassos = (idProduto: number): void =>
    setPassosAbertos((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(idProduto)) {
        proximo.delete(idProduto);
      } else {
        proximo.add(idProduto);
      }
      return proximo;
    });

  const aoSalvar = async (): Promise<void> => {
    try {
      await salvar({ idConfiguracaoCardapio: idConfig, ocultos }).unwrap();
      setRascunho(null);
      setSalvo(true);
    } catch {
      // O alerta de falha sai de `statusSalvar.isError`; o try existe para a
      // rejeição não subir como unhandled e sujar console/telemetria.
    }
  };

  const termo = busca.trim().toLowerCase();
  const filtrando = termo.length > 0 || soOcultos;

  /** Visível de fato: produto herda o grupo. */
  const produtoVisivel = (idGrupo: number, idProduto: number): boolean =>
    estaLigado('GRUPO', idGrupo) && estaLigado('PRODUTO', idProduto);

  const subPassoNaTela = mostraSubPasso(colunasAtivas);

  const temOpcaoOculta = (produto: ProdutoRecorte): boolean =>
    (produto.passos ?? []).some((passo) =>
      opcoesNaTela(passo, subPassoNaTela).some((opcao) => !estaLigado('OPCAO', opcao.id)),
    );

  const gruposFiltrados = useMemo(() => {
    let lista: GrupoRecorte[] = grupos;
    if (termo) {
      lista = lista
        .map((g) => ({ ...g, produtos: g.produtos.filter((p) => p.descricao.toLowerCase().includes(termo)) }))
        .filter((g) => g.descricao.toLowerCase().includes(termo) || g.produtos.length > 0);
    }
    if (soOcultos) {
      lista = lista
        .map((g) => ({ ...g, produtos: g.produtos.filter((p) => !produtoVisivel(g.id, p.id) || temOpcaoOculta(p)) }))
        .filter((g) => !estaLigado('GRUPO', g.id) || g.produtos.length > 0);
    }
    return lista;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grupos, termo, soOcultos, foraPorItem, alvos, colunasAtivas]);

  /**
   * Filtro novo abre o resultado sozinho — ele já é curto e o gestor quer ver o
   * que casou, não clicar grupo a grupo. Só até o teto (busca de uma letra casa
   * com o catálogo inteiro) e só quando o filtro muda: recolher continua valendo.
   */
  useEffect(() => {
    if (!filtrando) return;
    if (gruposFiltrados.length > MAX_ABRIR_SOZINHO) return;
    setAbertos(new Set(gruposFiltrados.map((g) => g.id)));
    if (soOcultos) {
      setPassosAbertos(new Set(gruposFiltrados.flatMap((g) => g.produtos.filter(temOpcaoOculta).map((p) => p.id))));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termo, soOcultos]);

  /** O que não aparece numa faixa. Grupo oculto conta como um, não pelos produtos. */
  const contarEscondidos = (colunasDaFaixa: ColunaRecorte[]): ContagemOcultos =>
    alvos.length
      ? {
        ...grupos.reduce(
          (total, g) =>
            estaLigado('GRUPO', g.id, colunasDaFaixa)
              ? {
                ...total,
                itens:
                  total.itens + g.produtos.filter((p) => !estaLigado('PRODUTO', p.id, colunasDaFaixa)).length,
              }
              : { ...total, grupos: total.grupos + 1 },
          VAZIO,
        ),
        opcoes: idsDasOpcoesNas(colunasDaFaixa).filter((id) => !estaLigado('OPCAO', id, colunasDaFaixa)).length,
      }
      : VAZIO;

  const escondidosAqui = contarEscondidos(colunasAtivas);

  const rotuloSelecao = selecao.startsWith(PREFIXO_TIPO)
    ? `Todos os pontos de ${selecao.slice(PREFIXO_TIPO.length)}`
    : (alvos[0] ? rotuloSemConsumo(alvos[0]) : '');

  /** Aba do que está selecionado, para a trilha. */
  const tipoAtual = selecao.startsWith(PREFIXO_TIPO) ? selecao.slice(PREFIXO_TIPO.length) : (alvos[0]?.grupo ?? '')
  const pontosDoTipoAtual = colunas.filter((c) => c.grupo === tipoAtual)

  const seletorDeConsumo = faixas.length > 1 && (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Ajustando o cardápio de
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        {faixas.map((faixa, indice) => (
          <CartaoConsumo
            key={faixa.rotulo}
            icone={faixa.consumo === 'LEVAR' ? <ShoppingBagIcon /> : <RestaurantIcon />}
            rotulo={faixa.rotulo ?? ''}
            escondidos={contarEscondidos(faixa.colunas)}
            ativo={indice === indiceConsumo}
            aoEscolher={() => setIndiceConsumo(indice)}
          />
        ))}
      </Stack>
    </Box>
  )

  const barraSalvar = alterado && (
    <Paper
      elevation={0}
      sx={(t) => ({
        position: 'sticky',
        bottom: 0,
        zIndex: 2,
        mt: 2,
        p: 1.5,
        borderRadius: 1,
        borderTop: 1,
        borderColor: 'divider',
        // Sombra só para CIMA: a barra fica colada no rodapé, então é na borda
        // de cima que o conteúdo passa por baixo e precisa de separação. Mesmo
        // padrão da barra de salvar das Configurações.
        boxShadow: t.palette.mode === 'dark' ? '0 -6px 14px rgba(0,0,0,0.45)' : '0 -6px 14px rgba(0,0,0,0.06)',
      })}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
      >
        <Typography variant="body2" color="text.secondary">
          {alteracoes === 1 ? '1 item alterado' : `${alteracoes} itens alterados`} — ainda não salvo
        </Typography>
        <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Button
            onClick={() => {
              setRascunho(null);
              setSalvo(false);
            }}
            disabled={statusSalvar.isLoading}
            fullWidth
          >
            Descartar
          </Button>
          <Button variant="contained" onClick={aoSalvar} disabled={statusSalvar.isLoading} fullWidth>
            {statusSalvar.isLoading ? 'Salvando…' : 'Salvar'}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );

  if (!isLoading && colunas.length === 0) {
    return (
      <Box>
        <HeaderTela titulo="Visibilidade dos itens" />
        <Alert severity="info">
          Esta loja ainda não tem nenhum ponto de acesso ativo. Crie um em "Acessos ao cardápio" para escolher o que
          aparece em cada um.
        </Alert>
      </Box>
    );
  }

  const avisos = (
    <>
      {salvo && !alterado && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSalvo(false)}>
          Visibilidade atualizada.
        </Alert>
      )}
      {statusSalvar.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Não foi possível salvar. Tente de novo.
        </Alert>
      )}
    </>
  );

  // ===== Painel: por onde começar =====
  if (!selecao) {
    const totalOculto = totalOcultos([...ocultosPorColuna.values()].reduce(somarOcultos, VAZIO));
    return (
      <Box>
        <HeaderTela
          titulo="Visibilidade dos itens"
          descricao="Tudo que está cadastrado no Sischef aparece em todos os pontos de acesso. Aqui você desliga o que não deve aparecer em cada um — o cadastro no Sischef não é alterado."
        />
        {avisos}

        {isLoading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <PainelInfo
                titulo="Por onde começar"
                itens={[
                  totalOculto === 0 ? (
                    <>
                      <strong>Nenhum item está oculto</strong>: todos os pontos mostram o cardápio
                      completo. Escolha um ponto abaixo para ajustar o que aparece nele.
                    </>
                  ) : (
                    <>
                      Escolha abaixo o ponto de acesso que você quer ajustar. O número ao lado de cada um
                      mostra quantos itens já estão ocultos ali.
                    </>
                  ),
                  <>
                    Ocultar vale <strong>só para o ponto escolhido</strong>: o mesmo produto pode aparecer
                    no delivery e sumir do totem, sem mexer no cadastro dele no Sischef.
                  </>,
                ]}
              />
            </Box>

            {pontosPorTipo.map(([tipo, pontos]) => (
              <Box key={tipo} sx={{ mb: 3 }}>
                {/* Sem ícone aqui: ele fica só no que é clicável (os cards), para
                    o painel não virar uma parede de símbolos repetidos. */}
                <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {tipo}
                </Typography>
                <Stack spacing={1}>
                  {pontos.map((ponto) => {
                    const porColuna = ponto.colunas.map((c) => ({
                      coluna: c,
                      contagem: ocultosPorColuna.get(c.chave) ?? VAZIO,
                    }));
                    const contagem = porColuna.reduce((total, c) => somarOcultos(total, c.contagem), VAZIO);
                    const qtd = totalOcultos(contagem);
                    // Totem: a conta é por consumo, senão "5 itens ocultos" não
                    // diz se some para quem come aqui, para quem leva, ou nos dois.
                    const detalhe =
                      ponto.colunas.length > 1
                        ? porColuna
                          .map(
                            ({ coluna, contagem: c }) =>
                              `${ROTULO_CONSUMO[coluna.consumo as ConsumoTotem]}: ${rotuloOcultosCurto(c)}`,
                          )
                          .join(' · ')
                        : rotuloOcultos(contagem);
                    return (
                      <CartaoPonto
                        key={ponto.chave}
                        icone={<IconeDoTipo tipo={tipo} />}
                        titulo={ponto.rotulo}
                        detalhe={detalhe}
                        destacado={qtd > 0}
                        aoClicar={() => setSelecao(ponto.chave)}
                      />
                    );
                  })}
                  {/* Com mais de um ponto, evita repetir o mesmo ajuste em cada um. */}
                  {pontos.length > 1 && (
                    <CartaoPonto
                      icone={<IconeDoTipo tipo={tipo} />}
                      titulo={`Editar os ${pontos.length} pontos de ${tipo} juntos`}
                      detalhe="A mudança vale para todos eles"
                      aoClicar={() => setSelecao(`${PREFIXO_TIPO}${tipo}`)}
                    />
                  )}
                </Stack>
              </Box>
            ))}
          </>
        )}
        {barraSalvar}
      </Box>
    );
  }

  // ===== Lista do ponto escolhido =====
  const podeVoltar = colunas.length > 1;

  return (
    <Box>
      {/* Fixa abaixo da barra do topo: num catálogo grande a lista é longa, e a
          altura de rolagem é justamente onde se esquece em que ponto de acesso
          se está mexendo — que é o que decide o efeito de cada switch. */}
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 3,
          bgcolor: 'background.default',
          // Sangra até a borda da área de conteúdo para o que rola por baixo
          // não aparecer nas laterais.
          mx: { xs: -2, md: -3 },
          px: { xs: 2, md: 3 },
          py: 1,
          mb: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="Onde você está">
          {podeVoltar ? (
            <Link
              component="button"
              type="button"
              underline="hover"
              color="inherit"
              variant="body2"
              onClick={() => {
                setSelecao('');
                setBusca('');
                setSoOcultos(false);
              }}
            >
              Pontos de acesso
            </Link>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Pontos de acesso
            </Typography>
          )}

          {tipoAtual &&
            (pontosDoTipoAtual.length > 1 && !selecao.startsWith(PREFIXO_TIPO) ? (
              // Atalho para editar o tipo inteiro — é o passo natural de quem
              // percebe que a mudança vale para todos os totens, não só um.
              <Link
                component="button"
                type="button"
                underline="hover"
                color="inherit"
                variant="body2"
                onClick={() => setSelecao(`${PREFIXO_TIPO}${tipoAtual}`)}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <IconeDoTipo tipo={tipoAtual} fontSize="small" />
                {tipoAtual}
              </Link>
            ) : (
              // Sem ícone: aqui o tipo é só rótulo, não leva a lugar nenhum.
              <Typography variant="body2" color="text.secondary">
                {tipoAtual}
              </Typography>
            ))}

          <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
            {selecao.startsWith(PREFIXO_TIPO) ? `Todos os pontos (${alvos.length})` : rotuloSelecao}
          </Typography>
        </Breadcrumbs>
      </Box>

      <HeaderTela
        titulo="Visibilidade dos itens"
        descricao={
          selecao.startsWith(PREFIXO_TIPO)
            ? `A mudança vale para os ${alvos.length} pontos de ${selecao.slice(PREFIXO_TIPO.length)} de uma vez. Nada é alterado no cadastro do Sischef.`
            : 'Ative ou desative os itens deste ponto de acesso. Nada é alterado no cadastro do Sischef.'
        }
        acoes={
          colunas.length > 1 ? (
            <FormControl size="small" sx={{ minWidth: { sm: 240 } }} fullWidth>
              <InputLabel id="rotulo-ponto">Ponto de acesso</InputLabel>
              <Select
                labelId="rotulo-ponto"
                label="Ponto de acesso"
                value={selecao}
                onChange={(e) => setSelecao(e.target.value)}
              >
                {/* Achatado num array só: o Select do MUI não enxerga
                    ListSubheader dentro de Fragment. */}
                {pontosPorTipo.flatMap(([tipo, pontos]) => [
                  <ListSubheader key={`h:${tipo}`}>{tipo}</ListSubheader>,
                  ...pontos.map((p) => (
                    <MenuItem key={p.chave} value={p.chave}>
                      {p.rotulo}
                    </MenuItem>
                  )),
                  ...(pontos.length > 1
                    ? [
                      <MenuItem key={`t:${tipo}`} value={`${PREFIXO_TIPO}${tipo}`}>
                        Todos os pontos de {tipo}
                      </MenuItem>,
                    ]
                    : []),
                ])}
              </Select>
            </FormControl>
          ) : undefined
        }
      />

      {avisos}

      {seletorDeConsumo}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ sm: 'center' }}
        sx={{ mb: 1 }}
      >
        <TextField
          placeholder="Buscar grupo ou produto"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          size="small"
          fullWidth
          sx={{ flex: 1, minWidth: 0 }}
          InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
        />
        {/* Largura própria: no flex do lado da busca os dois botões encolhiam
            até o texto e ficavam espremidos na quina da tela. */}
        <ToggleButtonGroup
          size="small"
          exclusive
          fullWidth
          value={soOcultos ? 'ocultos' : 'todos'}
          onChange={(_e, valor) => valor && setSoOcultos(valor === 'ocultos')}
          sx={{ flexShrink: 0, width: { xs: '100%', sm: 300 } }}
        >
          <ToggleButton value="todos">Todos</ToggleButton>
          <ToggleButton value="ocultos">Só ocultos</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Typography color="text.secondary" variant="body2" sx={{ flex: 1 }}>
          {totalOcultos(escondidosAqui) === 0 ? 'Nada oculto aqui' : rotuloOcultos(escondidosAqui)}
        </Typography>
        {/* O atalho que ficava em "Mostrar tudo em: [No local] [Para levar]": com
            um cardápio por vez ele é um botão só, do que está na tela. */}
        {faixas.length > 1 && totalOcultos(escondidosAqui) > 0 && (
          <Button
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => definir(todosOsItens, true)}
          >
            Mostrar tudo
          </Button>
        )}
        <Button
          size="small"
          startIcon={abertos.size > 0 ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
          onClick={() =>
            setAbertos((atual) =>
              atual.size > 0 ? new Set() : new Set(gruposFiltrados.map((g) => g.id)),
            )
          }
          disabled={gruposFiltrados.length === 0}
        >
          {abertos.size > 0 ? 'Recolher todos' : 'Expandir todos'}
        </Button>
      </Stack>

      <Stack direction="row" spacing={0.75} sx={{ mb: 2 }}>
        <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', mt: '2px' }} />
        <Typography color="text.secondary" variant="caption">
          {selecao.startsWith(PREFIXO_TIPO)
            ? `A mudança vale para os ${alvos.length} pontos deste tipo.`
            : 'Ocultar o item em todos os pontos da loja tira ele do cardápio.'}{' '}
          Ocultar um produto não tira ele dos passos de outros produtos (ex.: refrigerante dentro de um combo). Para tirar
          uma opção de um passo, abra os passos do produto: ela sai só desse passo e, se o passo for vinculado a outros
          produtos, sai deles também.
        </Typography>
      </Stack>

      {isLoading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : gruposFiltrados.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {soOcultos && !termo
              ? 'Nenhum item oculto neste ponto — o cardápio está completo.'
              : filtrando
                ? 'Nenhum grupo ou produto encontrado.'
                : 'Nenhum item marcado como "Mostrar no cardápio" no Sischef. A marcação fica no cadastro do produto e do grupo.'}
          </Typography>
        </Paper>
      ) : (
        // A lista precisa do contêiner próprio: o Accordion do MUI desenha um
        // divisor `::before` no topo e só o suprime em `:first-of-type` — que é
        // "primeira <div> entre os irmãos", não "primeiro accordion". Soltos aqui,
        // o divisor do primeiro grupo virava um risco órfão embaixo do texto de
        // ajuda. Com o Box, a regra do próprio MUI passa a valer.
        <Box>
          {gruposFiltrados.map((grupo: GrupoRecorte) => {
            const aberto = abertos.has(grupo.id);
            const grupoLigado = estaLigado('GRUPO', grupo.id);
            const ocultosDentro = grupo.produtos.filter((p) => !estaLigado('PRODUTO', p.id)).length;
            const pausadosDentro = grupo.produtos.filter((p) => p.pausado).length;
            return (
              <Accordion
                key={grupo.id}
                disableGutters
                // Sem isto o MUI mantém os produtos de TODOS os grupos montados
                // (o Collapse só zera a altura): milhares de controles vivos
                // numa loja com catálogo grande, e a tela engasga.
                TransitionProps={{ unmountOnExit: true }}
                expanded={aberto}
                onChange={(_e, expandido) =>
                  setAbertos((atual) => {
                    const proximo = new Set(atual);
                    if (expandido) proximo.add(grupo.id);
                    else proximo.delete(grupo.id);
                    return proximo;
                  })
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, pr: 2, minWidth: 0 }}>
                    <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex' }}>
                      <Switch
                        size="small"
                        checked={grupoLigado}
                        onChange={(e) => definir([{ tipo: 'GRUPO', id: grupo.id }], e.target.checked)}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        noWrap
                        sx={{
                          fontWeight: 500,
                          textDecoration: grupoLigado ? undefined : 'line-through',
                          color: grupoLigado ? undefined : 'text.disabled',
                        }}
                      >
                        {grupo.descricao}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {grupo.produtos.length === 1 ? '1 produto' : `${grupo.produtos.length} produtos`}
                        {grupoLigado && ocultosDentro > 0 && ` · ${ocultosDentro} ocultos`}
                        {/* No resumo do grupo para não obrigar a abrir um por um
                          só para descobrir que tem item pausado lá dentro. */}
                        {pausadosDentro > 0 && ` · ${pausadosDentro} pausados`}
                      </Typography>
                    </Box>
                    {/* Neutro: grupo oculto é uma escolha do gestor, não um erro —
                      o vermelho do tema alarmaria à toa. O texto riscado e
                      apagado já diz o estado. */}
                    {!grupoLigado && <Chip label="Oculto" size="small" variant="outlined" />}
                    {ocultoNaOutra('GRUPO', grupo.id) && (
                      <Chip
                        icon={<VisibilityOffIcon />}
                        label={avisoOutraFaixa}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {grupo.produtos.length === 0 ? (
                    <Typography color="text.secondary" variant="body2">
                      {filtrando ? 'Nenhum produto neste grupo bate com o filtro.' : 'Nenhum produto neste grupo.'}
                    </Typography>
                  ) : (
                    <>
                      {/* Ícones e "os N produtos" no rótulo: como texto puro os
                        dois botões não diziam sobre o que agiam — o gestor lia
                        como se valesse para a tela inteira. */}
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ pl: 3, pb: 1, flexWrap: 'wrap', rowGap: 1 }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Neste grupo:
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          color="inherit"
                          startIcon={<VisibilityIcon />}
                          disabled={!grupoLigado}
                          onClick={() =>
                            definir(
                              grupo.produtos.map((p) => ({ tipo: 'PRODUTO' as Tipo, id: p.id })),
                              true,
                            )
                          }
                        >
                          Mostrar os {grupo.produtos.length} produtos
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="inherit"
                          startIcon={<VisibilityOffIcon />}
                          disabled={!grupoLigado}
                          onClick={() =>
                            definir(
                              grupo.produtos.map((p) => ({ tipo: 'PRODUTO' as Tipo, id: p.id })),
                              false,
                            )
                          }
                        >
                          Ocultar os {grupo.produtos.length} produtos
                        </Button>
                      </Stack>
                      {grupo.produtos.map((p) => {
                        const ligado = estaLigado('PRODUTO', p.id);
                        const visivel = ligado && grupoLigado;
                        const passos = p.passos ?? [];
                        const passosAbertosAqui = passosAbertos.has(p.id);
                        return (
                          <Box key={p.id}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1.5}
                              sx={{ pl: 3, py: 0.25, '&:hover': { bgcolor: 'action.hover' } }}
                            >
                              <Switch
                                size="small"
                                checked={visivel}
                                disabled={!grupoLigado}
                                onChange={(e) => definir([{ tipo: 'PRODUTO', id: p.id }], e.target.checked)}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  textDecoration: visivel ? undefined : 'line-through',
                                  color: visivel ? undefined : 'text.disabled',
                                }}
                              >
                                {p.descricao}
                              </Typography>
                              {/* Só a marcação do próprio produto: grupo fora do outro
                                cardápio já é dito na linha do grupo. */}
                              {ocultoNaOutra('PRODUTO', p.id) && (
                                <Chip
                                  icon={<VisibilityOffIcon />}
                                  label={avisoOutraFaixa}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                              {/* Ligado aqui e pausado no Sischef = fora do cardápio
                                assim mesmo. Sem o aviso, é o clássico "liguei e não
                                aparece". Só informa: pausa se tira no Sischef. */}
                              {p.pausado && <ChipPausado />}
                              {passos.length > 0 && (
                                <>
                                  <Box sx={{ flex: 1 }} />
                                  <Button
                                    size="small"
                                    color="inherit"
                                    aria-expanded={passosAbertosAqui}
                                    onClick={() => alternarPassos(p.id)}
                                    endIcon={
                                      <ExpandMoreIcon
                                        sx={{ transform: passosAbertosAqui ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
                                      />
                                    }
                                    sx={{ textTransform: 'none', color: 'text.secondary' }}
                                  >
                                    {passos.length === 1 ? '1 passo' : `${passos.length} passos`}
                                  </Button>
                                </>
                              )}
                            </Stack>
                            {passosAbertosAqui &&
                              passos.map((passo) => (
                                <PassoDoProduto
                                  key={passo.id}
                                  passo={passo}
                                  mostrarSubPasso={subPassoNaTela}
                                  produtoVisivel={visivel}
                                  estaLigada={(id) => estaLigado('OPCAO', id)}
                                  ocultaNaOutra={(id) => ocultoNaOutra('OPCAO', id)}
                                  avisoOutraFaixa={avisoOutraFaixa}
                                  aoDefinir={(ids, ligada) =>
                                    definir(ids.map((id) => ({ tipo: 'OPCAO' as Tipo, id })), ligada)
                                  }
                                />
                              ))}
                          </Box>
                        );
                      })}
                    </>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}

      {barraSalvar}
    </Box>
  );
}

function ChipPausado(): JSX.Element {
  return (
    <Tooltip title="Pausado no Sischef (ex.: sem estoque). Enquanto estiver assim, não aparece no cardápio mesmo ligado aqui. A pausa se tira no Sischef ou no app.">
      <Chip icon={<PauseCircleOutlineIcon />} label="Pausado" size="small" color="warning" variant="outlined" />
    </Tooltip>
  );
}

function mostraSubPasso(colunas: ColunaRecorte[]): boolean {
  return colunas.some((coluna) => coluna.mostraSubPasso === true);
}

function opcoesNaTela(passo: PassoRecorte, comSubPasso: boolean): OpcaoRecorte[] {
  if (!comSubPasso) {
    return passo.opcoes;
  }

  return [...passo.opcoes, ...(passo.subPasso?.opcoes ?? [])];
}

function PassoDoProduto({
  passo,
  mostrarSubPasso = false,
  ehSubPasso = false,
  produtoVisivel,
  estaLigada,
  ocultaNaOutra,
  avisoOutraFaixa,
  aoDefinir,
}: {
  passo: PassoRecorte;
  mostrarSubPasso?: boolean;
  ehSubPasso?: boolean;
  produtoVisivel: boolean;
  estaLigada: (idOpcao: number) => boolean;
  ocultaNaOutra: (idOpcao: number) => boolean;
  avisoOutraFaixa: string;
  aoDefinir: (idsOpcoes: number[], ligada: boolean) => void;
}): JSX.Element {
  const quantas = passo.opcoes.length;
  const ocultas = passo.opcoes.filter((opcao) => !estaLigada(opcao.id)).length;
  const ids = passo.opcoes.map((opcao) => opcao.id);

  return (
    <Box sx={{ pl: ehSubPasso ? 4 : 8, pt: 0.5, pb: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {passo.descricao}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {ehSubPasso && 'Adicionais dos itens do passo · '}
        {quantas === 1 ? '1 opção' : `${quantas} opções`}
        {produtoVisivel && ocultas > 0 && ` · ${ocultas} ${ocultas === 1 ? 'oculta' : 'ocultas'}`}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1, flexWrap: 'wrap', rowGap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Neste passo:
        </Typography>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<VisibilityIcon />}
          disabled={!produtoVisivel}
          onClick={() => aoDefinir(ids, true)}
        >
          {quantas === 1 ? 'Mostrar a opção' : `Mostrar as ${quantas} opções`}
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<VisibilityOffIcon />}
          disabled={!produtoVisivel}
          onClick={() => aoDefinir(ids, false)}
        >
          {quantas === 1 ? 'Ocultar a opção' : `Ocultar as ${quantas} opções`}
        </Button>
      </Stack>
      {passo.opcoes.map((opcao) => {
        const visivel = produtoVisivel && estaLigada(opcao.id);
        return (
          <Stack
            key={opcao.id}
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ py: 0.25, '&:hover': { bgcolor: 'action.hover' } }}
          >
            <Switch
              size="small"
              checked={visivel}
              disabled={!produtoVisivel}
              onChange={(e) => aoDefinir([opcao.id], e.target.checked)}
            />
            <Typography
              variant="body2"
              sx={{
                textDecoration: visivel ? undefined : 'line-through',
                color: visivel ? undefined : 'text.disabled',
              }}
            >
              {opcao.descricao}
            </Typography>
            {ocultaNaOutra(opcao.id) && (
              <Chip icon={<VisibilityOffIcon />} label={avisoOutraFaixa} size="small" variant="outlined" />
            )}
            {opcao.pausado && <ChipPausado />}
          </Stack>
        );
      })}
      {mostrarSubPasso && passo.subPasso && (
        <PassoDoProduto
          passo={passo.subPasso}
          ehSubPasso
          produtoVisivel={produtoVisivel}
          estaLigada={estaLigada}
          ocultaNaOutra={ocultaNaOutra}
          avisoOutraFaixa={avisoOutraFaixa}
          aoDefinir={aoDefinir}
        />
      )}
    </Box>
  );
}

/**
 * Ícone do tipo de ponto de acesso.
 *
 * Os mesmos das abas da tela de Configuração geral (`pages/admin/configuracao`,
 * linhas 196-204) — é onde o gestor aprende a associação, e duas telas com
 * ícones diferentes para o mesmo canal custam mais do que a liberdade de
 * escolher um símbolo melhor. Exceção: "Checkout" não existe como aba lá, e o
 * caixa é o que ele é.
 *
 * As chaves são as abas montadas no backend (`ABA_DO_TIPO` em
 * `colunas-recorte.ts`); tipo novo cai no genérico em vez de quebrar a tela.
 */
function IconeDoTipo({ tipo, ...props }: { tipo: string } & SvgIconProps): JSX.Element {
  switch (tipo) {
    case 'Delivery':
      return <DeliveryDiningIcon {...props} />;
    case 'Vitrine':
      return <StorefrontIcon {...props} />;
    case 'Totem':
      return <SlideshowIcon {...props} />;
    case 'Checkout':
      return <PointOfSaleIcon {...props} />;
    case 'Comanda/Mesa':
      return <TableRestaurantIcon {...props} />;
    default:
      return <DevicesOtherIcon {...props} />;
  }
}

/**
 * Cartão do seletor de consumo do totem de pedidos.
 *
 * Mesma forma dos cartões de ponto do painel (Paper + ButtonBase): é a segunda
 * escolha que o gestor faz nesta tela e não deveria parecer outro tipo de
 * controle. A contagem embaixo evita escolher às cegas.
 */
function CartaoConsumo({
  icone,
  rotulo,
  escondidos,
  ativo,
  aoEscolher,
}: {
  icone: ReactNode;
  rotulo: string;
  escondidos: ContagemOcultos;
  ativo: boolean;
  aoEscolher: () => void;
}): JSX.Element {
  return (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        overflow: 'hidden',
        borderColor: ativo ? 'primary.main' : 'divider',
        bgcolor: ativo ? 'action.selected' : undefined,
      }}
    >
      <ButtonBase
        onClick={aoEscolher}
        aria-pressed={ativo}
        sx={{ width: '100%', p: 1.75, justifyContent: 'flex-start', textAlign: 'left' }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', color: ativo ? 'primary.main' : 'text.secondary' }}>{icone}</Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontWeight: ativo ? 700 : 500 }}>
              {rotulo}
            </Typography>
            <Typography
              variant="body2"
              color={totalOcultos(escondidos) > 0 ? 'warning.main' : 'text.secondary'}
            >
              {rotuloOcultos(escondidos)}
            </Typography>
          </Box>
        </Stack>
      </ButtonBase>
    </Paper>
  );
}

/** Card de ponto de acesso do painel inicial. */
function CartaoPonto({
  icone,
  titulo,
  detalhe,
  destacado,
  aoClicar,
}: {
  icone?: ReactNode;
  titulo: string;
  detalhe: string;
  destacado?: boolean;
  aoClicar: () => void;
}): JSX.Element {
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <ButtonBase
        onClick={aoClicar}
        sx={{
          width: '100%',
          p: 1.75,
          justifyContent: 'space-between',
          textAlign: 'left',
          transition: (t) => t.transitions.create('background-color', { duration: 200 }),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
          {icone && <Box sx={{ display: 'flex', color: 'text.secondary' }}>{icone}</Box>}
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontWeight: 500 }}>
              {titulo}
            </Typography>
            <Typography variant="body2" color={destacado ? 'warning.main' : 'text.secondary'}>
              {detalhe}
            </Typography>
          </Box>
        </Stack>
        <ChevronRightIcon color="action" />
      </ButtonBase>
    </Paper>
  );
}
